import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar, { NavItem } from "./Sidebar";
import { Permission } from "../permissions/permission.enum";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Button from "./ui/Button";
import {
  FaSun,
  FaMoon,
  FaGlobe,
  FaStar,
  FaSlidersH,
  FaComments,
  FaFacebook,
} from "react-icons/fa";

interface LayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  title?: string;
  showSidebar?: boolean;
  menuPosition?: "left" | "top";
}

const Layout = ({
  children,
  showBackButton = false,
  title,
  showSidebar = true,
  menuPosition = "left",
}: LayoutProps) => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const toggleLanguage = () => {
    const nextLang = i18n.language === "en" ? "tc" : "en";
    i18n.changeLanguage(nextLang);
  };

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("cms_sidebar_collapsed");
      if (stored === "true") setIsSidebarCollapsed(true);
      if (stored === "false") setIsSidebarCollapsed(false);
    } catch {
      setIsSidebarCollapsed(false);
    }
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("cms_sidebar_collapsed", String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const navItems: NavItem[] = [
    {
      label: t("card_interests"),
      to: "/card-interests",
      permission: Permission.CARD_INTERESTS_READ,
      icon: <FaStar />,
    },
    {
      label: t("channel_config"),
      to: "/channel-config",
      permission: Permission.CARD_INTERESTS_UPDATE,
      icon: <FaSlidersH />,
    },
    {
      label: t("whatsapp_listings"),
      to: "/whatsapp-listings",
      permission: Permission.LISTINGS_READ,
      icon: <FaComments />,
    },
    {
      label: t("facebook_listings"),
      to: "/facebook-listings",
      permission: Permission.LISTINGS_READ,
      icon: <FaFacebook />,
    },
  ];

  const visibleNavItemsFlat = navItems
    .flatMap((item) => (item.children ? item.children : [item]))
    .filter((item) => !item.permission || hasPermission(item.permission));

  const getHeaderContent = (containerClassName: string) => (
    <div
      className={[
        containerClassName,
        "h-16 flex items-center justify-between",
      ].join(" ")}
    >
      <div className="flex items-center gap-4 min-w-0">
        {showBackButton && (
          <button
            onClick={handleBack}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("back")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
        )}
        {title && (
          <h1 className="text-xl font-semibold tracking-tight truncate">
            {title}
          </h1>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={toggleLanguage}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-1"
          aria-label={t("switch_language")}
          title={t("switch_language")}
        >
          <FaGlobe className="w-5 h-5" />
          <span className="text-sm font-medium">
            {i18n.language === "en" ? "EN" : "繁"}
          </span>
        </button>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          aria-label={theme === "dark" ? t("light_mode") : t("dark_mode")}
          title={theme === "dark" ? t("light_mode") : t("dark_mode")}
        >
          {theme === "dark" ? (
            <FaSun className="w-5 h-5" />
          ) : (
            <FaMoon className="w-5 h-5" />
          )}
        </button>
        {isAuthenticated && (
          <Button variant="secondary" size="sm" onClick={logout}>
            {t("logout")}
          </Button>
        )}
      </div>
    </div>
  );

  const headerContent =
    menuPosition === "top"
      ? getHeaderContent("w-full px-4 sm:px-6 lg:px-8")
      : getHeaderContent("max-w-7xl mx-auto px-4 sm:px-6 lg:px-8");

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200 font-sans flex flex-col">
      {menuPosition === "left" ? (
        <div className="flex min-h-screen">
          {showSidebar && (
            <Sidebar
              items={navItems}
              collapsed={isSidebarCollapsed}
              onToggleCollapsed={toggleSidebarCollapsed}
            />
          )}

          <div className="flex-1 flex flex-col min-w-0">
            <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10 flex-shrink-0">
              {headerContent}
            </header>

            <main className="flex-1 flex flex-col w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {children}
            </main>
          </div>
        </div>
      ) : (
        <div className="flex flex-col min-h-screen">
          <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10">
            {headerContent}
            {showSidebar && (
              <div className="border-t border-gray-200 dark:border-gray-700 overflow-x-auto">
                <nav className="w-full px-2 sm:px-4 lg:px-6 py-2 flex items-center gap-1 whitespace-nowrap">
                  {visibleNavItemsFlat.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to!}
                      className={({ isActive }) =>
                        [
                          "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                          isActive
                            ? "bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700",
                        ].join(" ")
                      }
                    >
                      <span className="text-lg" aria-hidden>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </nav>
              </div>
            )}
          </header>

          <main className="w-full px-4 sm:px-6 lg:px-8 py-8">{children}</main>
        </div>
      )}
    </div>
  );
};

export default Layout;
